import {
  ClockIcon,
  DevicePhoneMobileIcon,
  LanguageIcon,
  PuzzlePieceIcon
} from '@heroicons/react/20/solid'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  useLanguageContext,
  usePartnerContext,
  useUserContext
} from '../../context'
import { useSystemQuery } from '../../hooks/use-system-query'
import { useAnswersByOwnerQuery } from '../../hooks/useAnswersByOwnerQuery'
import Badge from '../tailwind/Badge'
import Panel from '../tailwind/Panel'

export default function AssessmentMonitor({ space }) {
  const { language } = useLanguageContext()
  const { partner } = usePartnerContext()
  const { cognitoUser } = useUserContext()
  const { t } = useTranslation()

  const { data: answersByOwner } = useAnswersByOwnerQuery({
    owner: space?.owner
  })
  const { data: system } = useSystemQuery({ username: cognitoUser?.username })

  // Check if Assessment Monitor is visible based on partner settings
  if (!partner.isDashboardComponentVisible?.assessmentMonitor) {
    return null
  }

  // Show loading only if system data is not available yet
  if (!system?.challenges?.data) {
    return (
      <Panel className={'mt-4'}>
        <h2 className='text-lg font-medium leading-6 text-gray-900'>
          {t('talent.assessment_monitor.title')}
        </h2>
        <p className='mt-1 w-full max-w-4xl text-sm text-gray-500 sm:w-9/12'>
          {t('talent.assessment_monitor.loading')}
        </p>
      </Panel>
    )
  }

  const challenges = Object.values(JSON.parse(system.challenges.data))

  // Show message when no assessment data is available yet
  if (!answersByOwner || answersByOwner.length === 0) {
    return (
      <Panel className={'mt-4'}>
        <div className='flex w-full flex-row items-center justify-between'>
          <h2 className='flex-col items-center justify-center text-lg font-medium leading-6 text-primaryBlue'>
            {t('talent.assessment_monitor.title')}
          </h2>
          <Badge text={t('global.new')} color={'pink'} type={'basic'} />
        </div>
        <p className='mt-1 w-full text-sm text-gray-500'>
          {t('talent.assessment_monitor.description')}
        </p>
        <div className='mt-4 rounded-lg bg-gray-50 p-6 text-center'>
          <p className='text-sm text-gray-600'>
            {t('talent.assessment_monitor.no_data_yet')}
          </p>
        </div>
      </Panel>
    )
  }

  function formatTime(ms, t) {
    const totalSeconds = Math.floor(ms / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    if (minutes === 0 && seconds === 0)
      return t('talent.assessment_monitor.no_data')
    return `${minutes} min ${seconds} sek`
  }

  return (
    <Panel className={'mt-4'}>
      <div className='flex w-full flex-row items-center justify-between'>
        <h2 className='flex-col items-center justify-center text-lg font-medium leading-6 text-primaryBlue'>
          {t('talent.assessment_monitor.title')}
        </h2>
        <Badge text={t('global.new')} color={'pink'} type={'basic'} />
      </div>

      <p className='mt-1 w-full text-sm text-gray-500'>
        {t('talent.assessment_monitor.description')}
      </p>

      <ul
        role='list'
        className='mt-4 h-96 flex-col divide-y divide-gray-100 overflow-y-scroll'
      >
        {answersByOwner.map((answer) => {
          const assessment = challenges.find(
            (challenge) => challenge.exam_id === answer.exam_id
          )

          return (
            <div
              key={answer.id}
              className='mt-2 overflow-hidden bg-white px-4 py-6 shadow-sm sm:rounded-lg sm:px-6'
            >
              <h3 className='text-base font-medium text-gray-900'>
                {`${assessment?.title?.[language] || t('talent.assessment_monitor.assessment')}`}
              </h3>
              <div className='mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-sm text-gray-600'>
                <span className='font-semibold'>
                  <ClockIcon className='mr-1 inline-block size-4 text-primaryBlue' />
                  {`${t('talent.assessment_monitor.time_needed')}:`}
                </span>
                <span>{formatTime(answer.total_time_needed_in_ms, t)}</span>
                <span className='font-semibold'>
                  <PuzzlePieceIcon className='mr-1 inline-block size-4 text-primaryBlue' />
                  {t('talent.assessment_monitor.attempts')}:
                </span>
                <span>{answer.attempts || 1}</span>

                <span className='font-semibold'>
                  <LanguageIcon className='mr-1 inline-block size-4 text-primaryBlue' />
                  {t('talent.assessment_monitor.language')}:
                </span>
                <span>{t(`language.${answer.language || 'de'}`)}</span>

                <span className='font-semibold'>
                  <DevicePhoneMobileIcon className='mr-1 inline-block size-4 text-primaryBlue' />
                  {t('talent.assessment_monitor.operating_system')}:
                </span>
                <span>{answer.os}</span>
              </div>
            </div>
          )
        })}
      </ul>
    </Panel>
  )
}

AssessmentMonitor.propTypes = {
  space: PropTypes.object,
  career: PropTypes.object
}
